import { useState } from "react";
import { GigCard } from "@/pages/Dashboard";
import { GigBrief } from "@/components/GigBrief";
import { SmartFillScheduleReview } from "@/components/SmartFillScheduleReview";
import { RevenueOverview } from "@/components/RevenueOverview";
import { DragListenersContext } from "@/hooks/useDragHandle";
import { tokens } from "@/design-system/tokens";

// ──────────────────────────────────────────────────────────────────────
// Light component harness — renders GigCard with mock data across every
// state, with NO auth required, so design-sync changes can be verified
// visually before commit (the live app is auth-gated and bounces to prod).
//
// Route: /dev/components  (public, see App.js)
// Add more components here as they get reworked from Claude Design.
// ──────────────────────────────────────────────────────────────────────

const tk = tokens;

// Mock gigs cover the conditional-render matrix that prototypes drop:
// payment pill hidden for future-pending, shown for past/invoiced/paid,
// multiday date range, long-name ellipsis, cancelled status.
const FUTURE = "2026-07-10";
const PAST = "2026-05-10";

const MOCKS = [
  { label: "Future · confirmed · pending (no payment pill)",
    gig: { gig_id: "1", artist_name: "58th Street Stompers", venue_name: "The Lansdowne Club", city: "London", date: FUTURE, start_time: "19:10", status: "confirmed", fee: 340, fee_status: "pending" } },
  { label: "Past · confirmed · pending (Pending pill)",
    gig: { gig_id: "2", artist_name: "London Dance Orchestra", venue_name: "The Ned London", city: "London", date: PAST, start_time: "19:00", status: "confirmed", fee: 850, fee_status: "pending" } },
  { label: "Invoiced",
    gig: { gig_id: "3", artist_name: "Renée Okafor Quartet", venue_name: "Café Oto", city: "London", date: PAST, start_time: "20:30", status: "confirmed", fee: 600, fee_status: "invoiced" } },
  { label: "Paid",
    gig: { gig_id: "4", artist_name: "The Midnight Collective", venue_name: "Blue Note", city: "Berlin", date: PAST, start_time: "21:00", status: "confirmed", fee: 1000, fee_status: "paid" } },
  { label: "Pending status",
    gig: { gig_id: "5", artist_name: "Saskatchewan Lighthouse Orchestra", venue_name: "Bimhuis", city: "Amsterdam", date: FUTURE, start_time: "19:45", status: "pending", fee: 2400, fee_status: "pending" } },
  { label: "Cancelled",
    gig: { gig_id: "6", artist_name: "The Quiet Storm", venue_name: "Ronnie Scott's", city: "London", date: FUTURE, start_time: "20:00", status: "cancelled", fee: 500, fee_status: "pending" } },
  { label: "Multiday (date range)",
    gig: { gig_id: "7", artist_name: "Transatlantic Big Band", venue_name: "Le Trianon", city: "Paris", date: "2026-07-21", start_time: "20:00", status: "confirmed", fee: 1800, fee_status: "pending", is_multiday: true, travel_date: "2026-07-20", return_date: "2026-07-22" } },
];

// Sample gigs for the Next Gig Brief. Entirely fictional — no real user data.
// Two scenarios prove the Dress code / Notes / Gig contacts behaviour: short
// content renders inline on one line; long content falls back to a dropdown.
const BRIEF_BASE = {
  date: "2026-07-15",
  call_time: "18:00",
  start_time: "20:00",
  end_time: "23:00",
  venue_name: "The Jazz Café",
  venue_address: "5 Parkway, London",
  city: "London",
  is_multiday: false,
  share_token: null,
  gig_finished: false,
};

const BRIEF_SHORT = {
  ...BRIEF_BASE,
  gig_id: "brief-short",
  fee: 250,
  dress_code: "All black",
  description: "Soundcheck with Alex Rivera, Sam Bell.",
};
const BRIEF_SHORT_COLLABS = [
  { collaborator_id: "1", name: "Alex Rivera", instrument: "Guitar", role: "Guitar" },
  { collaborator_id: "2", name: "Sam Bell", instrument: "Drums", role: "Drums" },
];

const BRIEF_LONG = {
  ...BRIEF_BASE,
  gig_id: "brief-long",
  fee: 600,
  dress_code: "Smart — dark suit, jacket required for the second set",
  description:
    "Two 45-minute sets, backline provided. Confirm the stage plot with Alex Rivera, Sam Bell, Jordan Lee and Casey Moon before doors at 7.",
};
const BRIEF_LONG_COLLABS = [
  ...BRIEF_SHORT_COLLABS,
  { collaborator_id: "3", name: "Jordan Lee", instrument: "Bass", role: "Bass" },
  { collaborator_id: "4", name: "Casey Moon", instrument: "Keys", role: "Keys" },
];

// Mock /gigs/parse-schedule payload — a rehearsal block plus the gig, one
// engagement-wide fee, one duplicate, and one row the parser couldn't date.
const SCHEDULE_PARSED = {
  shared: { title: "58th Street Stompers", venue_name: "The Ritz", city: "Manchester",
            dress_code: "Black tie", total_fee: 1200, notes: null },
  total: 5,
  duplicates: 1,
  truncated: false,
  events: [
    { gig_type: "rehearsal", title: "58th Street Stompers", venue_name: "The Ritz", city: "Manchester",
      date: "2026-09-01", start_time: "14:00", finish_time: "18:00", call_time: null, fee: null,
      dress_code: null, notes: null, schedule: [], is_duplicate: false, venue_candidates: [] },
    { gig_type: "rehearsal", title: "58th Street Stompers", venue_name: "The Ritz", city: "Manchester",
      date: "2026-09-02", start_time: "10:00", finish_time: "14:00", call_time: null, fee: null,
      dress_code: null, notes: null, schedule: [], is_duplicate: false, venue_candidates: [] },
    { gig_type: "rehearsal", title: "58th Street Stompers", venue_name: "Band Room", city: "Manchester",
      date: "2026-09-03", start_time: "11:00", finish_time: null, call_time: null, fee: null,
      dress_code: null, notes: null, schedule: [], is_duplicate: true, venue_candidates: [] },
    { gig_type: "public", title: "58th Street Stompers", venue_name: "The Ritz", city: "Manchester",
      date: "2026-09-04", end_date: "2026-09-05", start_time: "20:00", finish_time: "23:00", call_time: "18:30", fee: 450,
      dress_code: null, notes: null, schedule: [], is_duplicate: false, venue_candidates: [],
      same_day: { label: "Dep singer dep", venue_name: "The Ned", gig_type: "function", count: 1 } },
    { gig_type: "function", title: "58th Street Stompers", venue_name: "Private client", city: null,
      date: null, start_time: null, finish_time: null, call_time: null, fee: null,
      dress_code: null, notes: "date TBC", schedule: [], is_duplicate: false, venue_candidates: [] },
  ],
};

// Mock GET /revenue/stats. Anchored on 2026-08 with gigs booked in only some of
// the six months ahead, so the £0 (flat-forecast) months are visible too.
const REVENUE_STATS = {
  total_fees: 18400, total_expenses: 2100, net_income: 16300, total_pending: 1250,
  current_month: { month: "2026-08", fees: 1450 },
  current_tax_year: { year: "2026/2027", fees: 9750 },
  monthly_breakdown: {
    "2026-09": { fees: 2400, expenses: 150, net: 2250 },
    "2026-10": { fees: 900,  expenses: 0,   net: 900 },
    "2026-12": { fees: 3200, expenses: 400, net: 2800 },
    "2027-01": { fees: 300,  expenses: 450, net: -150 },
  },
};

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: tk.font.mono, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: tk.color.textMuted, margin: "8px 0" }}>
    {children}
  </div>
);

export default function ComponentHarness() {
  const [selected, setSelected] = useState(new Set(["2"]));
  // Live-editable inputs for the Next Gig Brief so the inline-vs-dropdown
  // threshold can be exercised without a backend. Contacts are derived from
  // names mentioned in the note — exactly as the real app does it.
  const [briefDress, setBriefDress] = useState("All black");
  const [briefNote, setBriefNote] = useState("Soundcheck with Alex Rivera, Sam Bell.");
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const noop = () => {};
  const [scheduleOpen, setScheduleOpen] = useState(false);

  return (
    <div style={{ minHeight: "100vh", background: tk.color.appBg, color: tk.color.text, fontFamily: tk.font.body, padding: "32px 22px 80px" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <div style={{ fontFamily: tk.font.mono, fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", color: tk.color.textDim, marginBottom: 8 }}>
          Fretless · Dev harness
        </div>
        <h1 style={{ fontFamily: tk.font.display, fontWeight: 600, fontSize: 36, letterSpacing: "-0.03em", margin: 0 }}>GigCard</h1>
        <p style={{ color: tk.color.textDim, fontSize: 14.5, lineHeight: 1.55, maxWidth: 620 }}>
          Every state, no auth. Verify design-sync changes here before commit.
        </p>

        {/* Revenue Overview */}
        <SectionLabel>Revenue Overview · tax year + 6-month forecast</SectionLabel>
        <div style={{ maxWidth: 560, marginBottom: 20 }}>
          {/* On the dashboard this context comes from SortableGridItem; the
              widget's drag handle throws without it, so supply an inert one. */}
          <DragListenersContext.Provider value={{ attributes: {}, listeners: {} }}>
            <RevenueOverview revenueStats={REVENUE_STATS}
                             unpaidInvoices={{ count: 2, total: 1250 }}
                             navigate={noop} onHide={noop} />
          </DragListenersContext.Provider>
        </div>

        {/* Multi-date Smart Fill review */}
        <SectionLabel>Smart Fill · multi-date review modal</SectionLabel>
        <button
          onClick={() => setScheduleOpen(true)}
          style={{ fontFamily: tk.font.body, fontSize: 14, padding: "8px 14px", borderRadius: 8,
                   border: `1px solid ${tk.color.textMuted}`, background: "transparent",
                   color: tk.color.text, cursor: "pointer", marginBottom: 12 }}
          data-testid="harness-open-schedule"
        >
          Open schedule review (5 events)
        </button>
        <SmartFillScheduleReview
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          parsed={SCHEDULE_PARSED}
          onCreated={noop}
        />

        {/* Selectable, accounting NOT connected */}
        <SectionLabel>Selectable · accounting not connected</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {MOCKS.map(({ label, gig }) => (
            <div key={gig.gig_id}>
              <div style={{ fontSize: 11, color: tk.color.textMuted, marginBottom: 6 }}>{label}</div>
              <GigCard gig={gig} onEdit={noop} selectable selected={selected.has(gig.gig_id)} onSelectToggle={toggle} />
            </div>
          ))}
        </div>

        {/* Accounting connected — FA + Xero buttons (only show for pending) */}
        <SectionLabel>FreeAgent + Xero connected (pending gigs show FA / X)</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {MOCKS.filter((m) => m.gig.fee_status === "pending" && m.gig.status !== "cancelled").map(({ label, gig }) => (
            <div key={`acc-${gig.gig_id}`}>
              <div style={{ fontSize: 11, color: tk.color.textMuted, marginBottom: 6 }}>{label}</div>
              <GigCard gig={gig} onEdit={noop} selectable selected={false} onSelectToggle={noop} accStatus={{ freeagent: true, xero: true }} />
            </div>
          ))}
        </div>

        {/* Not selectable (dashboard context) */}
        <SectionLabel>Not selectable (dashboard context)</SectionLabel>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {MOCKS.slice(0, 3).map(({ gig }) => (
            <GigCard key={`ns-${gig.gig_id}`} gig={gig} onEdit={noop} />
          ))}
        </div>

        {/* ── Next Gig Brief ────────────────────────────────────────────── */}
        <h2 style={{ fontFamily: tk.font.display, fontWeight: 600, fontSize: 28, letterSpacing: "-0.03em", margin: "48px 0 0" }}>
          Next Gig Brief
        </h2>
        <p style={{ color: tk.color.textDim, fontSize: 14.5, lineHeight: 1.55, maxWidth: 620 }}>
          Sample gigs, no auth. Resize the window past 900px to see the desktop
          layout. Dress code / Notes / Gig contacts read inline on one line, and
          fall back to a dropdown only when the content is too long to fit.
        </p>

        {/* Live playground — edits update the brief instantly (no save needed) */}
        <SectionLabel>Live playground — type to flip inline ↔ dropdown</SectionLabel>
        <div style={{ display: "grid", gap: 12, maxWidth: 620, marginBottom: 16, padding: 16, background: tk.color.cardBg, border: tk.border.hairline, borderRadius: tk.radius.md }}>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, color: tk.color.textMuted }}>Dress code (short → inline; long → dropdown)</span>
            <input
              value={briefDress}
              onChange={(e) => setBriefDress(e.target.value)}
              style={{ background: tk.color.innerBg, border: tk.border.inner, borderRadius: tk.radius.sm, color: tk.color.text, padding: "8px 10px", fontFamily: tk.font.body, fontSize: 14 }}
            />
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, color: tk.color.textMuted }}>
              Notes (short → inline; long → dropdown). Mention Alex Rivera, Sam Bell, Jordan Lee or Casey Moon to tag contacts.
            </span>
            <textarea
              value={briefNote}
              onChange={(e) => setBriefNote(e.target.value)}
              rows={3}
              style={{ background: tk.color.innerBg, border: tk.border.inner, borderRadius: tk.radius.sm, color: tk.color.text, padding: "8px 10px", fontFamily: tk.font.body, fontSize: 14, resize: "vertical" }}
            />
          </label>
        </div>
        <GigBrief
          gig={{ ...BRIEF_BASE, gig_id: "brief-live", fee: 350, dress_code: briefDress, description: briefNote }}
          collaborators={BRIEF_LONG_COLLABS}
          travelMinutes={45}
          onHide={noop}
        />

        <div style={{ height: 32 }} />

        {/* Fixed reference examples */}
        <SectionLabel>Example · short note + 2 contacts → all inline (no dropdowns)</SectionLabel>
        <GigBrief gig={BRIEF_SHORT} collaborators={BRIEF_SHORT_COLLABS} travelMinutes={45} onHide={noop} />

        <div style={{ height: 24 }} />

        <SectionLabel>Example · long note + 4 contacts → Dress code, Notes &amp; Gig contacts all as dropdowns</SectionLabel>
        <GigBrief gig={BRIEF_LONG} collaborators={BRIEF_LONG_COLLABS} travelMinutes={45} onHide={noop} />
      </div>
    </div>
  );
}
